import { useEffect } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { onErrorGraphql } from "utils/onErrorGraphql";
import { clientNeoFact, clientUploadNeoFact } from "@neonumy/api-clients";
import {
  LIST_EMAIL_EXTRA_FILES,
  GET_EMAIL_EXTRA_FILES,
  CREATE_EMAIL_EXTRA_FILE,
  UPDATE_EMAIL_EXTRA_FILE,
  DELETE_EMAIL_EXTRA_FILE,
} from "queries/neoFact/settings";

export interface EmailExtraFile {
  id: string;
  fileUrl: string;
  label: string;
  isActiveBudget: boolean;
  isActiveSaleOrder: boolean;
  isActiveDeliveryForm: boolean;
  isActivePurchaseOrder: boolean;
  isActiveReturn: boolean;
  isActiveReceipt: boolean;
  isActivePaymentInAdvance: boolean;
}

export default function useEmailAttachmentsApi() {
  const [
    listEmailExtraFiles,
    {
      loading: listLoading,
      refetch,
      data: listEmailExtraFilesData,
      error: listEmailExtraFilesError,
    },
  ] = useLazyQuery<ListEmailExtraFilesResponse, ListEmailExtraFilesVars>(LIST_EMAIL_EXTRA_FILES, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (listEmailExtraFilesError) onErrorGraphql(listEmailExtraFilesError);
  }, [listEmailExtraFilesError]);

  const extraFiles: EmailExtraFile[] = (listEmailExtraFilesData?.listEmailExtraFiles?.results ??
    []) as unknown as EmailExtraFile[];

  const [getEmailExtraFiles, { loading: getLoading, error: getEmailExtraFilesError }] =
    useLazyQuery<GetEmailExtraFilesResponse, GetEmailExtraFilesVars>(GET_EMAIL_EXTRA_FILES, {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    });

  useEffect(() => {
    if (getEmailExtraFilesError) onErrorGraphql(getEmailExtraFilesError);
  }, [getEmailExtraFilesError]);

  const [createEmailExtraFile, { loading: createLoading }] = useMutation<
    CreateEmailExtraFileResponse,
    CreateEmailExtraFileVars
  >(CREATE_EMAIL_EXTRA_FILE, {
    client: clientUploadNeoFact,
    fetchPolicy: "no-cache",
  });

  const [updateEmailExtraFile, { loading: updateLoading }] = useMutation<
    UpdateEmailExtraFileResponse,
    UpdateEmailExtraFileVars
  >(UPDATE_EMAIL_EXTRA_FILE, {
    client: clientUploadNeoFact,
    fetchPolicy: "no-cache",
  });

  const [deleteEmailExtraFile, { loading: deleteLoading }] = useMutation<
    DeleteEmailExtraFileResponse,
    DeleteEmailExtraFileVars
  >(DELETE_EMAIL_EXTRA_FILE, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  return {
    extraFiles,
    listEmailExtraFiles,
    listLoading,
    refetch,
    getEmailExtraFiles,
    getLoading,
    createEmailExtraFile,
    createLoading,
    updateEmailExtraFile,
    updateLoading,
    deleteEmailExtraFile,
    deleteLoading,
  };
}
